/**
 * undercover® — productpagina.
 *
 * - live-updates: elementen met [data-uc-live] worden na een variantkeuze vervangen door de
 *   versie uit de nieuwe HTML die Horizon al ophaalt (ProductSelectEvent → detail.html)
 * - zachte crossfade van de hoofdafbeelding bij een kleurwissel
 * - drawers (<dialog class="uc-drawer">) via [data-uc-open] / [data-uc-close]
 * - <uc-size-help>: maatcalculator die de maat in de variantkiezer selecteert
 * - <uc-ship-countdown>: eerlijke verzendcountdown (Europe/Amsterdam, werkdagen)
 * - popover EN 1078 (+ Esc/klik buiten)
 * - <uc-badge-addon>: badge-keuze, live preview op de foto, helm + badge in één keer
 * - <uc-bought-together>: vinkjes, totaal, alles tegelijk toevoegen
 * - contactformulieren (mail me …) via fetch, met gewone submit als terugval
 * - "aanbevolen voor jou" (sessionStorage uc_quiz_result)
 * - <uc-product-related>: lazy aanbevelingen
 * - <uc-product-3d>: 3D-model + AR in een dialoog
 */
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const track = (name, data) => window.ucTrack?.(name, data);

const whenCart = () =>
  window.ucCart
    ? Promise.resolve(window.ucCart)
    : new Promise((resolve) => document.addEventListener('uc:cart-ready', () => resolve(window.ucCart), { once: true }));

/* ------------------------------------------------------------------------ */
/* Live-updates + crossfade na variantkeuze                                  */
/* ------------------------------------------------------------------------ */

const galleryImage = (root) => {
  const gallery = root.querySelector('media-gallery');
  if (!gallery) return null;
  const imgs = [...gallery.querySelectorAll('.product-media img, img')];
  // eerste zichtbare afbeelding in beeld (slideshow op mobiel, grid op desktop)
  return (
    imgs.find((img) => {
      const r = img.getBoundingClientRect();
      return r.width > 40 && r.height > 40 && r.right > 0 && r.left < window.innerWidth;
    }) || null
  );
};

function onProductSelect(event) {
  const section = event.currentTarget;
  if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog, uc-bought-together')) return;

  // Snapshot vóór Horizon de galerij vervangt
  const oldImg = galleryImage(section);
  const snapshot = oldImg
    ? { src: oldImg.currentSrc || oldImg.src, rect: oldImg.getBoundingClientRect(), fit: getComputedStyle(oldImg).objectFit }
    : null;

  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;

      section.querySelectorAll('[data-uc-live]').forEach((el) => {
        const fresh = html.querySelector(`[data-uc-live="${el.dataset.ucLive}"]`);
        if (fresh) el.replaceWith(document.importNode(fresh, true));
      });
      section.dispatchEvent(new CustomEvent('uc:variant-updated', { bubbles: true }));

      requestAnimationFrame(() => crossfade(section, snapshot));
    })
    .catch(() => {});
}

function crossfade(section, snapshot) {
  const img = galleryImage(section);
  if (img) {
    section.dispatchEvent(new CustomEvent('uc:gallery-updated', { bubbles: true }));
  }
  if (!snapshot || !img || reduceMotion()) return;
  const now = img.currentSrc || img.src;
  const base = (u) => (u || '').split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
  if (base(now) === base(snapshot.src)) return;

  const holder = img.closest('.product-media-container, li, .slideshow-slide') || img.parentElement;
  if (!holder) return;
  const hr = holder.getBoundingClientRect();
  const ghost = new Image();
  ghost.src = snapshot.src;
  ghost.alt = '';
  ghost.className = 'uc-xfade';
  Object.assign(ghost.style, {
    left: `${snapshot.rect.left - hr.left}px`,
    top: `${snapshot.rect.top - hr.top}px`,
    width: `${snapshot.rect.width}px`,
    height: `${snapshot.rect.height}px`,
    objectFit: snapshot.fit || 'contain',
  });
  if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
  holder.append(ghost);
  img.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)' });
  ghost
    .animate([{ opacity: 1 }, { opacity: 0 }], { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' })
    .finished.finally(() => ghost.remove());
}

const boundSections = new WeakSet();
function bindSections() {
  document.querySelectorAll('.shopify-section').forEach((section) => {
    if (boundSections.has(section) || !section.querySelector('.uc-ptitle, .uc-pprice, .uc-vpicker, .uc-tbuy')) return;
    boundSections.add(section);
    section.addEventListener(StandardEvents.productSelect, onProductSelect);
  });
}

/* ------------------------------------------------------------------------ */
/* Drawers                                                                   */
/* ------------------------------------------------------------------------ */

function openDrawer(dialog, opener) {
  if (!dialog || dialog.open) return;
  dialog._opener = opener;
  dialog.showModal();
  dialog.classList.add('is-open');
  dialog.dispatchEvent(new CustomEvent('uc:open'));
}

function closeDrawer(dialog) {
  if (!dialog?.open) return;
  dialog.classList.remove('is-open');
  const done = () => {
    dialog.close();
    dialog._opener?.focus?.();
  };
  if (reduceMotion()) return done();
  setTimeout(done, 260);
}

document.addEventListener('click', (event) => {
  const sizeOpener = event.target.closest('[data-uc-open-size]');
  if (sizeOpener) {
    const scope = sizeOpener.closest('.shopify-section') || document;
    openDrawer(scope.querySelector('uc-size-help dialog'), sizeOpener);
    return;
  }
  const opener = event.target.closest('[data-uc-open]');
  if (opener) {
    openDrawer(document.getElementById(opener.dataset.ucOpen), opener);
    return;
  }
  const closer = event.target.closest('[data-uc-close]');
  if (closer) {
    closeDrawer(closer.closest('dialog'));
    return;
  }
  // klik op de backdrop
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('uc-drawer')) {
    const r = event.target.querySelector('.uc-drawer__panel')?.getBoundingClientRect();
    if (r && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) {
      closeDrawer(event.target);
    }
  }
});

document.addEventListener(
  'cancel',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('uc-drawer')) {
      event.preventDefault();
      closeDrawer(event.target);
    }
  },
  true
);

/* ------------------------------------------------------------------------ */
/* Variant kiezen vanuit code (maatcalculator)                               */
/* ------------------------------------------------------------------------ */

const norm = (s) => String(s).toLowerCase().replace(/[\s–—]/g, (c) => (c === ' ' ? '' : '-'));

function selectOptionValue(scope, optionNames, matcher) {
  const inputs = [...scope.querySelectorAll('variant-picker input[data-option-name]')];
  const input = inputs.find(
    (el) => optionNames.includes(el.dataset.optionName.trim().toLowerCase()) && matcher(norm(el.value))
  );
  if (!input) return false;
  if (!input.checked) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  return true;
}

/* ------------------------------------------------------------------------ */
/* Maathulp + calculator                                                     */
/* ------------------------------------------------------------------------ */

class UcSizeHelp extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.range = this.querySelector('[data-uc-calc-range]');
    this.number = this.querySelector('[data-uc-calc-number]');
    this.result = this.querySelector('[data-uc-calc-result]');
    this.notify = this.querySelector('[data-uc-calc-notify]');
    this.strings = JSON.parse(this.querySelector('[data-uc-strings]')?.textContent || '""');
    const d = this.dataset;
    this.bounds = { smMin: +d.smMin, smMax: +d.smMax, mlMin: +d.mlMin, mlMax: +d.mlMax };

    this.range?.addEventListener('input', () => {
      this.number.value = this.range.value;
      this.update(false);
    });
    this.range?.addEventListener('change', () => this.update(true));
    this.number?.addEventListener('input', () => {
      if (this.number.value === '') return;
      this.range.value = this.number.value;
      clearTimeout(this._t);
      this._t = setTimeout(() => this.update(true), 450);
    });
    this.querySelector('dialog')?.addEventListener('uc:open', () => this.classList.add('is-drawn'));
  }

  sizeFor(cm) {
    const { smMin, smMax, mlMin, mlMax } = this.bounds;
    if (cm < smMin) return { out: 'low' };
    if (cm > mlMax) return { out: 'high' };
    // tussen smMax en mlMin (bijv. 54,5): de grotere maat, die draai je strakker
    if (cm <= smMax) return { key: `${smMin}-${smMax}`, label: 'S/M', range: `${smMin}–${smMax} cm`, row: 'sm' };
    return { key: `${mlMin}-${mlMax}`, label: 'M/L', range: `${mlMin}–${mlMax} cm`, row: 'ml' };
  }

  update(commit) {
    const cm = parseFloat(String(this.number.value || this.range.value).replace(',', '.'));
    if (Number.isNaN(cm)) return;
    const size = this.sizeFor(cm);
    this.querySelectorAll('.uc-sizetable tr[data-size]').forEach((tr) => tr.classList.toggle('is-match', tr.dataset.size === size.row));

    const tpl = this.querySelector(size.out ? `[data-uc-calc-${size.out}]` : '[data-uc-calc-ok]');
    this.result.replaceChildren(tpl.content.cloneNode(true));
    this.result.dataset.state = size.out || 'ok';
    if (!size.out) {
      this.result.querySelector('[data-size-label]').textContent = size.label;
      this.result.querySelector('[data-size-sub]').textContent = `${size.range}`;
    }
    if (this.notify) {
      this.notify.hidden = !size.out;
      const body = this.notify.querySelector('[data-uc-notify-body]');
      if (body) body.value = `hoofdomtrek ${cm} cm – graag bericht als er een passende maat is`;
    }

    if (!commit) return;
    const section = this.closest('.shopify-section') || document;
    let selected = false;
    if (!size.out) {
      selected = selectOptionValue(section, ['maat', 'size'], (v) => v.includes(size.key));
      if (selected) {
        const sub = this.result.querySelector('[data-size-sub]');
        if (sub && this.strings) sub.textContent = `${size.range} · ${this.strings.replace('[size]', size.label)}`;
      }
    }
    if (this._lastTracked !== `${cm}`) {
      this._lastTracked = `${cm}`;
      track('size_calculator_used', { cm, size: size.label || `buiten bereik (${size.out})`, selected });
    }
  }
}
customElements.get('uc-size-help') || customElements.define('uc-size-help', UcSizeHelp);

/* ------------------------------------------------------------------------ */
/* Verzendcountdown (eerlijk: echte klok, echte cutoff, werkdagen)           */
/* ------------------------------------------------------------------------ */

const DAYS_NL = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

function amsterdamNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
  return { day, h: +get('hour'), m: +get('minute'), s: +get('second') };
}

class UcShipCountdown extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-ship-text]');
    this.sub = this.querySelector('[data-ship-sub]');
    this.render();
    this.timer = setInterval(() => this.render(), 1000 * 20);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  render() {
    const cutoff = Number(this.dataset.cutoff || 18);
    const { day, h, m, s } = amsterdamNow();
    const workday = day >= 1 && day <= 5;
    const secondsNow = h * 3600 + m * 60 + s;
    const cutoffSec = cutoff * 3600;
    const d = this.dataset;
    const pad = (n) => String(n).padStart(2, '0');
    const before = d.before.replace('[time]', `${pad(cutoff)}:00`);

    if (workday && secondsNow < cutoffSec) {
      const left = cutoffSec - secondsNow;
      const hh = Math.floor(left / 3600);
      const mm = Math.max(1, Math.ceil((left % 3600) / 60));
      const time = hh > 0 ? `${hh} u ${mm} min` : `${mm} min`;
      this.text.textContent = d.today;
      this.sub.textContent = d.left.replace('[time]', time);
      this.dataset.state = 'today';
      return;
    }

    // volgende werkdag
    let next = (day + 1) % 7;
    while (next === 0 || next === 6) next = (next + 1) % 7;
    const tomorrow = (day + 1) % 7 === next;
    if (tomorrow) this.text.textContent = d.tomorrow;
    else if (next === 1) this.text.textContent = d.monday;
    else this.text.textContent = d.day.replace('[day]', DAYS_NL[next]);
    this.sub.textContent = before;
    this.dataset.state = 'later';
  }
}
customElements.get('uc-ship-countdown') || customElements.define('uc-ship-countdown', UcShipCountdown);

/* ------------------------------------------------------------------------ */
/* Popover (EN 1078)                                                         */
/* ------------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-uc-pop]');
  document.querySelectorAll('[data-uc-pop][aria-expanded="true"]').forEach((open) => {
    if (open === btn) return;
    if (event.target.closest('.uc-pop') && open.parentElement.contains(event.target)) return;
    open.setAttribute('aria-expanded', 'false');
    document.getElementById(open.getAttribute('aria-controls'))?.setAttribute('hidden', '');
  });
  if (!btn) return;
  const pop = document.getElementById(btn.getAttribute('aria-controls'));
  const expanded = btn.getAttribute('aria-expanded') === 'true';
  btn.setAttribute('aria-expanded', String(!expanded));
  if (expanded) pop?.setAttribute('hidden', '');
  else pop?.removeAttribute('hidden');
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-uc-pop][aria-expanded="true"]').forEach((btn) => {
    btn.setAttribute('aria-expanded', 'false');
    document.getElementById(btn.getAttribute('aria-controls'))?.setAttribute('hidden', '');
    btn.focus();
  });
});

/* ------------------------------------------------------------------------ */
/* Badge-add-on                                                              */
/* ------------------------------------------------------------------------ */

class UcBadgeAddon extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.variants = JSON.parse(this.querySelector('[data-badge-variants]')?.textContent || '[]');
    this.toggle = this.querySelector('[data-badge-toggle]');
    this.panel = this.querySelector('[data-badge-panel]');
    this.error = this.querySelector('[data-badge-error]');
    this.ci = Number(this.dataset.colorIndex || 0);
    this.li = Number(this.dataset.letterIndex || 1);
    this.section = this.closest('.shopify-section') || document;

    this.toggle.addEventListener('change', () => this.setActive(this.toggle.checked));
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-badge-color]')) this.refreshLetters();
      if (e.target.matches('[data-badge-color], [data-badge-letter]')) {
        this.error.hidden = true;
        this.renderPreview();
      }
    });
    this.refreshLetters();

    this.onSubmit = this.onSubmit.bind(this);
    window.addEventListener('submit', this.onSubmit, true);
    this.section.addEventListener('uc:gallery-updated', () => this.placeOverlay());
    this.section.addEventListener('uc:variant-updated', () => this.placeOverlay());
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.onSubmit, true);
    this.overlay?.remove();
  }

  get color() {
    return this.querySelector('[data-badge-color]:checked')?.value || this.variants[0]?.options[this.ci];
  }

  get letter() {
    return this.querySelector('[data-badge-letter]:checked')?.value || '';
  }

  get variant() {
    return this.variants.find((v) => v.options[this.ci] === this.color && v.options[this.li] === this.letter);
  }

  setActive(on) {
    this.panel.hidden = !on;
    this.toggle.setAttribute('aria-expanded', String(on));
    this.classList.toggle('is-active', on);
    if (!on) this.error.hidden = true;
    this.renderPreview();
  }

  refreshLetters() {
    const color = this.color;
    this.querySelectorAll('[data-badge-letter]').forEach((input) => {
      const v = this.variants.find((x) => x.options[this.ci] === color && x.options[this.li] === input.value);
      const soldOut = !v || !v.available;
      input.disabled = soldOut;
      input.closest('label').classList.toggle('is-soldout', soldOut);
      if (soldOut && input.checked) input.checked = false;
    });
  }

  paint(svg, letter, color) {
    if (!svg) return;
    svg.dataset.kleur = /blauw|blue/i.test(color || '') ? 'blauw' : 'zwart';
    const text = svg.querySelector('text');
    if (text) text.textContent = letter || '?';
  }

  renderPreview() {
    const on = this.toggle.checked;
    this.paint(this.querySelector('.uc-badge__preview-svg svg'), this.letter, this.color);
    this.paint(this.querySelector('.uc-badge__mini svg'), this.letter || 'A', this.color);
    if (on) this.placeOverlay();
    else if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }
  }

  placeOverlay() {
    if (!this.toggle?.checked) return;
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery) return;
    const slides = [...gallery.querySelectorAll('.product-media-container')].filter((el) => el.getBoundingClientRect().width > 40);
    const holder = slides[0];
    if (!holder) return;
    if (!this.overlay || !holder.contains(this.overlay)) {
      this.overlay?.remove();
      this.overlay = this.querySelector('[data-badge-overlay]').content.firstElementChild.cloneNode(true);
      this.overlay.style.setProperty('--bx', `${this.dataset.x}%`);
      this.overlay.style.setProperty('--by', `${this.dataset.y}%`);
      if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
      holder.append(this.overlay);
      requestAnimationFrame(() => this.overlay.classList.add('is-visible'));
    } else {
      this.overlay.classList.add('is-visible');
    }
    this.paint(this.overlay.querySelector('svg'), this.letter || '?', this.color);
  }

  async onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.toggle.checked) return;
    if (form.dataset.type !== 'add-to-cart-form' || !this.section.contains(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const badge = this.variant;
    if (!badge) {
      this.error.textContent = this.dataset.pickLetter;
      this.error.hidden = false;
      this.panel.hidden = false;
      this.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      this.querySelector('[data-badge-letter]:not(:disabled)')?.focus();
      return;
    }

    const data = new FormData(form);
    const helmet = data.get('id');
    const quantity = Number(data.get('quantity') || 1);
    if (!helmet) return;
    const button = form.querySelector('[ref="addToCartButton"], button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('uc-is-loading');

    try {
      const cart = await whenCart();
      const label = this.dataset.propertyLabel || 'voor';
      await cart.add(
        [
          { id: helmet, quantity },
          { id: badge.id, quantity, properties: { [label]: this.dataset.productTitle } },
        ],
        { source: 'badge-addon', origin: form }
      );
      track('badge_addon_added', { letter: this.letter, color: this.color, helmet_variant: String(helmet), badge_variant: String(badge.id) });
    } catch (error) {
      /* ucCart toont zelf een toast */
    } finally {
      button?.removeAttribute('aria-busy');
      button?.classList.remove('uc-is-loading');
    }
  }
}
customElements.get('uc-badge-addon') || customElements.define('uc-badge-addon', UcBadgeAddon);

/* ------------------------------------------------------------------------ */
/* Vaak samen gekocht                                                        */
/* ------------------------------------------------------------------------ */

class UcBoughtTogether extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.button = this.querySelector('[data-together-add]');
    this.error = this.querySelector('[data-together-error]');
    this.addEventListener('change', (e) => {
      const select = e.target.closest('[data-together-select]');
      if (select) {
        const box = select.closest('li').querySelector('[data-together-item]');
        const opt = select.selectedOptions[0];
        box.dataset.id = opt.value;
        box.dataset.price = opt.dataset.price;
        box.closest('li').querySelector('.uc-together__price').firstChild.textContent = this.money(Number(opt.dataset.price));
      }
      this.update();
    });
    this.button.addEventListener('click', () => this.add());
    (this.closest('.shopify-section') || document).addEventListener('uc:variant-updated', () => this.update());
    this.update();
  }

  money(cents) {
    const total = this.querySelector('[data-together-total]');
    try {
      return formatMoney(cents, total.dataset.format || '{{amount}}', total.dataset.currency || 'EUR');
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  checked() {
    return [...this.querySelectorAll('[data-together-item]')].filter((i) => i.checked && !i.disabled);
  }

  update() {
    const items = this.checked();
    const sum = items.reduce((n, i) => n + Number(i.dataset.price || 0), 0);
    this.querySelector('[data-together-total]').textContent = this.money(sum);
    this.button.textContent = items.length > 1 ? this.dataset.labelMany : this.dataset.labelOne;
    this.button.disabled = items.length === 0;
    this.querySelectorAll('.uc-together__item').forEach((li) => {
      const box = li.querySelector('[data-together-item]');
      li.classList.toggle('is-off', !box?.checked);
    });
  }

  async add() {
    const items = this.checked();
    if (!items.length) {
      this.error.textContent = this.dataset.none;
      this.error.hidden = false;
      return;
    }
    this.error.hidden = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      const cart = await whenCart();
      await cart.add(items.map((i) => ({ id: i.dataset.id, quantity: 1 })), { source: 'bought-together', origin: this });
      if (items.length > 1) track('bundle_added', { items: items.map((i) => i.dataset.id), count: items.length });
    } catch {
      /* toast via ucCart */
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}
customElements.get('uc-bought-together') || customElements.define('uc-bought-together', UcBoughtTogether);

/* ------------------------------------------------------------------------ */
/* Contactformulieren (mail me …) zonder paginawissel                        */
/* ------------------------------------------------------------------------ */

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('form[data-uc-notify]');
  if (!form || form.dataset.native === 'true') return;
  event.preventDefault();
  const msg = form.querySelector('[data-uc-notify-msg]');
  const button = form.querySelector('button[type="submit"]');
  button?.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
    // captcha / challenge: dan toch de gewone route
    if (!response.ok || /challenge/.test(response.url)) throw new Error('native');
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    if (html.querySelector('.errors, .form__message--error, [id*="ContactForm"] .errors')) throw new Error('native');
    form.classList.add('is-sent');
    if (msg) msg.textContent = msg.dataset.ok;
    form.querySelector('input[type="email"]').value = '';
  } catch (error) {
    form.dataset.native = 'true';
    form.submit();
  } finally {
    button?.removeAttribute('aria-busy');
  }
});

/* ------------------------------------------------------------------------ */
/* "aanbevolen voor jou" (keuzehulp)                                         */
/* ------------------------------------------------------------------------ */

function quizLabel() {
  let data = null;
  try {
    data = JSON.parse(sessionStorage.getItem('uc_quiz_result') || 'null');
  } catch {
    return;
  }
  if (!data) return;
  const result = String(data.result || data.handle || data.product || '').toLowerCase().trim();
  if (!result) return;
  document.querySelectorAll('[data-uc-quiz-match]').forEach((el) => {
    const handle = el.dataset.ucQuizMatch.toLowerCase();
    const match = handle === result || handle.split('-').includes(result);
    const chip = el.querySelector('.uc-ptitle__quiz');
    if (chip) chip.hidden = !match;
  });
}

/* ------------------------------------------------------------------------ */
/* Gerelateerde producten (lazy)                                             */
/* ------------------------------------------------------------------------ */

class UcProductRelated extends HTMLElement {
  connectedCallback() {
    if (!this.querySelector('.uc-related__grid--loading')) return;
    const load = () => this.load();
    if (!('IntersectionObserver' in window)) return load();
    this.io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          load();
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  async load() {
    try {
      const text = await fetch(this.dataset.url).then((r) => r.text());
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('uc-product-related');
      if (fresh?.querySelector('[data-related-items]:not(.uc-related__grid--loading)')) {
        this.querySelector('.uc-container').replaceWith(fresh.querySelector('.uc-container'));
      } else {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    } catch {
      this.closest('.shopify-section')?.setAttribute('hidden', '');
    }
  }
}
customElements.get('uc-product-related') || customElements.define('uc-product-related', UcProductRelated);

/* ------------------------------------------------------------------------ */
/* 3D + AR                                                                   */
/* ------------------------------------------------------------------------ */

class UcProduct3d extends HTMLElement {
  connectedCallback() {
    this.querySelector('dialog')?.addEventListener('uc:open', () => this.load(), { once: true });
  }

  load() {
    const stage = this.querySelector('[data-3d-stage]');
    const tpl = this.querySelector('[data-3d-template]');
    if (!stage || !tpl) return;
    stage.append(tpl.content.cloneNode(true));
    const models = JSON.parse(this.querySelector('[data-3d-models]')?.textContent || '[]');
    window.Shopify?.loadFeatures?.([
      { name: 'model-viewer-ui', version: '1.0', onLoad: () => {} },
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: () => {
          if (!window.ShopifyXR) return;
          window.ShopifyXR.addModels(models);
          window.ShopifyXR.setupXRElements();
        },
      },
    ]);
  }
}
customElements.get('uc-product-3d') || customElements.define('uc-product-3d', UcProduct3d);

/* ------------------------------------------------------------------------ */
/* Start                                                                     */
/* ------------------------------------------------------------------------ */

function init() {
  bindSections();
  quizLabel();
}

init();
document.addEventListener('shopify:section:load', init);
